import React, { useReducer } from 'react';
import { Trans, useI18nContext } from '@ecg-marktplaats/js-react-i18n';
import BundleSelect from '../../components/BundleSelect/BundleSelect';
import BundleOption from '../../components/BundleSelect/BundleOption';
import Faq from '../../components/Faqs/Faq';
import BundleSellingPoints from '../../components/BundleSellingPoints/BundleSellingPoints';
import BundleHero from '../../components/BundleHero/BundleHero';
import BundleSocialProof from '../../components/BundleSocialProof/BundleSocialProof';
import FeatureBlock, { variants as FeatureBlockVariants } from '../../components/FeatureOverview/FeatureBlock';

import orderBundleRequest from '../../services/orderBundle';
import { initiatePaymentUrl } from '../../constants/urls';
import requestStatus from '../../constants/requestStatus';
import { EnvironmentProvider } from '../../contexts/EnvironmentContext';
import { getInitialState, reducer, CustomError } from './reducer';
import { ErrorInpage, PrimaryButton } from '@hz-design-system/web-ui';
import { AppState, EnvironmentConfig } from '../../../../../types/AppState';

const PurchaseBundleApp = ({ appState, environment }: { appState: AppState; environment: EnvironmentConfig }) => {
  const {
    dataObject: { tablePurchaseBundle, faqs },
    seoBlurb,
  } = appState;
  const { xsrfToken, loggedInUser } = environment;

  const [state, dispatch] = useReducer(reducer, getInitialState());
  const i18n = useI18nContext();

  const orderBundle = async ({ bundleType }: { bundleType: string }) => {
    try {
      dispatch({ type: 'initiate' });
      const { orderId } = await orderBundleRequest({ xsrfToken, bundleType });
      dispatch({ type: 'success', payload: orderId });
      return window.location.assign(initiatePaymentUrl(orderId));
    } catch (err) {
      dispatch({ type: 'failure', payload: err as CustomError });
    }
  };

  const redirectToLogInScreen = () =>
    window.location.assign(`/account/login.html?target=${encodeURIComponent(window.location.href)}`);

  const hasSocialProofContent = i18n.tExists('purchaseBundle.socialProof.title');

  return (
    <EnvironmentProvider value={environment}>
      <div className="hz-PurchaseBundle">
        <div className="hz-PurchaseBundle-section hz-PurchaseBundle-hero">
          <div className="hz-PurchaseBundle-container">
            <BundleHero />
          </div>
        </div>
        <div className="hz-PurchaseBundle-section">
          <div className="hz-PurchaseBundle-container">
            <BundleSellingPoints />
          </div>
        </div>
        <div id="hz-PurchaseBundle-bundles" className="hz-PurchaseBundle-section">
          <div className="hz-PurchaseBundle-container">
            <div className="hz-PurchaseBundle-wrapBundles">
              <div className="hz-PurchaseBundle-bundlesHeaderWrap">
                <h1 className="hz-text-header1">{i18n.t('purchaseBundle.bundles.header_line_1')}</h1>
                <p>{i18n.t('purchaseBundle.bundles.header_line_2')}</p>
              </div>
              {state.status === requestStatus.failure && (
                <ErrorInpage icon="alert" description={state.error as unknown as string} />
              )}
              <BundleSelect>
                {tablePurchaseBundle.map((bundle) => (
                  <BundleOption
                    key={bundle.bundleType}
                    bundleType={bundle.bundleType}
                    isBundleRecommended={bundle.isBundleRecommended}
                    bundleLabel={bundle.bundleLabel}
                    marketFitText={bundle.marketFitText}
                    isDisabled={state.status === requestStatus.fetching}
                    onSelect={loggedInUser ? orderBundle : redirectToLogInScreen}
                    originalPrice={bundle.originalPrice}
                    effectivePrice={bundle.effectivePrice}
                    features={bundle.features}
                    isPriceDiscounted={bundle.isPriceDiscounted}
                    discountPercentage={bundle.discountPercentage}
                    isCurrentActiveBundle={bundle.isCurrentActiveBundle}
                  />
                ))}
              </BundleSelect>
              <div className="hz-PurchaseBundles-wrapBundles--disclaimer">
                <p>{i18n.t('purchaseBundle.bundleDisclaimers.item1')}</p>
                <div className="hz-PurchaseBundle-disclaimerAsterisks">
                  <p>{i18n.t('purchaseBundle.bundleDisclaimers.item2')}</p>
                  <p>{i18n.t('purchaseBundle.bundleDisclaimers.item3')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="hz-PurchaseBundle-section">
          <div className="hz-PurchaseBundle-container">
            <div className="hz-PurchaseBundle-wrapBundles">
              <div className="hz-PurchaseBundle-featureOverview">
                <h2>
                  <Trans i18nKey="purchaseBundle.featureOverview.header" />
                </h2>
                <FeatureBlock
                  variant={FeatureBlockVariants.verifiedSeller}
                  title={i18n.t('purchaseBundle.featureOverview.verifiedSeller.title')}
                  body={i18n.t('purchaseBundle.featureOverview.verifiedSeller.body')}
                />
                <FeatureBlock
                  variant={FeatureBlockVariants.sellerPage}
                  title={i18n.t('purchaseBundle.featureOverview.sellerPage.title')}
                  body={i18n.t('purchaseBundle.featureOverview.sellerPage.body')}
                />
                <FeatureBlock
                  variant={FeatureBlockVariants.fotoknaller}
                  title={i18n.t('purchaseBundle.featureOverview.fotoknaller.title')}
                  body={i18n.t('purchaseBundle.featureOverview.fotoknaller.body')}
                />
                <FeatureBlock
                  isPlus
                  variant={FeatureBlockVariants.opvalsticker}
                  title={i18n.t('purchaseBundle.featureOverview.opvalsticker.title')}
                  body={i18n.t('purchaseBundle.featureOverview.opvalsticker.body')}
                />
                <FeatureBlock
                  isPlus
                  variant={FeatureBlockVariants.gallery}
                  title={i18n.t('purchaseBundle.featureOverview.gallery.title')}
                  body={i18n.t('purchaseBundle.featureOverview.gallery.body')}
                />
              </div>
            </div>
          </div>
        </div>
        {hasSocialProofContent && (
          <div className="hz-PurchaseBundle-section">
            <div className="hz-PurchaseBundle-container">
              <BundleSocialProof />
            </div>
          </div>
        )}
        <div className="hz-PurchaseBundle-section">
          <div className="hz-PurchaseBundle-container">
            <div className="hz-PurchaseBundle-faqsWrap">
              <h2>{i18n.t('purchaseBundle.faqs.header')}</h2>
              {faqs.map((faq, index) => (
                <Faq key={`faq-${index}`} question={faq.question} answer={faq.answer} />
              ))}
            </div>
          </div>
        </div>
        <div className="hz-PurchaseBundle-section">
          <div className="hz-PurchaseBundle-container">
            <div className="hz-PurchaseBundle-bottomCta">
              <div className="hz-PurchaseBundle-bottomCta-message">
                {i18n.t('purchaseBundle.ctas.bottomCta.message')}
              </div>
              <PrimaryButton as="a" href="#hz-PurchaseBundle-bundles">
                {i18n.t('purchaseBundle.ctas.bottomCta.buttonAction')}
              </PrimaryButton>
            </div>
          </div>
        </div>
        <div className="hz-PurchaseBundle-section">
          <div className="hz-PurchaseBundle-container">
            <p className="hz-text-paragraph">{seoBlurb}</p>
          </div>
        </div>
      </div>
    </EnvironmentProvider>
  );
};

export default PurchaseBundleApp;
